/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * 실험 데이터 Mock Store
 * 프로젝트/실험/Run 데이터를 목업으로 제공합니다.
 */
import { GOLD_DATA } from '@/shared/api/demo/data/icebergData';
import type { ExecuteRequest, ExecutionResponse, ExecutionStatus } from '@/shared/api/execution/types';
import type { Library } from '@/shared/types';

import type { AssetSearchResultItem } from '@/entities/asset-search/domain/types';
import type {
  ProjectInfo,
  ProjectsExperimentsResponse,
  RunAssetCounts,
  RunInfo,
} from '@/entities/experiment/domain/types';

import type { IntegrationSearchResponse } from '@/features/dataSearch/api/integrationSearchApi';

type MockRun = RunInfo & {
  projectId: number;
  experimentId: number;
  projectName: string;
  experimentName: string;
  counts: RunAssetCounts;
};

type MockRunWithAssets = MockRun & {
  featureSetAsset?: AssetSearchResultItem;
  datasetAsset?: AssetSearchResultItem;
  parameterSet?: {
    id: number;
    runId: number;
    hyperparameters: Record<string, string | number | boolean>;
    description?: string;
    topMatchedValue?: string;
  };
  artifacts?: {
    id: number;
    count: number;
    runUuid?: string;
    description?: string;
    topMatchedValue?: string;
    files: Array<{
      path: string;
      isDir: boolean;
      fileSize?: number;
      type?: string;
      typeId?: string;
      content?: string | object;
    }>;
  };
  metrics?: Record<string, number>;
  experimentTags?: Record<string, string>;
  model?: {
    modelId: string;
    modelFormat?: string | null;
    servingConfig?: {
      backend?: string | null;
      convertToOnnx?: boolean | null;
    } | null;
  } | null;
};

const toExperimentTagList = (tags?: Record<string, string>): string[] | null => {
  if (!tags) return null;
  return Object.entries(tags).map(([key, value]) => `${key}:${value}`);
};

const DEMO_RUN_WORKBENCH_FILES = {
  'test_valid_imports.py': `"""
테스트용 파일: venv_packages에 있는 라이브러리만 사용하는 예시
"""

import matplotlib.pyplot as plt
import numpy as np
import pandas as pd
import requests
from scipy import stats
from sklearn.ensemble import RandomForestClassifier
from sklearn.metrics import accuracy_score
from sklearn.model_selection import train_test_split


def preprocess_data(df):
    z_scores = np.abs(stats.zscore(df.select_dtypes(include=[np.number])))
    outliers = (z_scores > 3).any(axis=1)
    print(f"이상치 개수: {outliers.sum()}")
    return df.fillna(df.mean(numeric_only=True))


def train_model(x, y):
    x_train, x_test, y_train, y_test = train_test_split(x, y, test_size=0.2, random_state=42)
    model = RandomForestClassifier(n_estimators=100, max_depth=10, random_state=42)
    model.fit(x_train, y_train)
    y_pred = model.predict(x_test)
    print(f"모델 정확도: {accuracy_score(y_test, y_pred):.4f}")
    return model
`,
  'write_your_model_experiment.py': `def write_your_model_experiment(
    dataset: pd.DataFrame | None = None,
    featureset: pd.DataFrame | None = None,
    parameterset: dict[str, Any] | None = None,
) -> ExperimentLogBundle:
    import torch.nn as nn
    import torch.optim as optim
    from torch.utils.data import DataLoader, TensorDataset

    params: dict[str, Any] = dict(parameterset) if parameterset is not None else {}
    metrics: dict[str, float] = {}
    metric_series: list[MetricRecord] = []
    model_input_sample: Any | None = None
    model: Any | None = None
    output_datasets: list[OutputDataset] = []
    artifact_files: list[ArtifactFile] = []
    parameterset_description: str = ""
    artifacts_description: str = ""

    if dataset is None:
        raise ValueError("실험을 위한 dataset이 제공되지 않았습니다.")

    df = dataset.copy()
    if "acq_time" in df.columns:
        df["acq_time"] = pd.to_datetime(df["acq_time"]).astype(np.int64) // 10**9

    feature_cols = ["acq_time", "current_u", "current_v", "current_w"]
    x_data = df[feature_cols].values
    y_data = df["current_u"].shift(-1).fillna(0).values

    x_tensor = torch.tensor(x_data, dtype=torch.float32)
    y_tensor = torch.tensor(y_data, dtype=torch.float32).view(-1, 1)

    params.setdefault("lr", 0.001)
    params.setdefault("epochs", 20)
    params.setdefault("batch_size", 64)
    params.setdefault("hidden_dim", 32)

    loader = DataLoader(TensorDataset(x_tensor, y_tensor), batch_size=params["batch_size"], shuffle=True)
    model_input_sample = x_tensor[:5].detach().cpu().numpy()
    model = nn.Sequential(nn.Linear(len(feature_cols), params["hidden_dim"]), nn.ReLU(), nn.Linear(params["hidden_dim"], 1))
    criterion = nn.MSELoss()
    optimizer = optim.Adam(model.parameters(), lr=params["lr"])

    for epoch in range(params["epochs"]):
        model.train()
        epoch_loss = 0.0
        for batch_x, batch_y in loader:
            optimizer.zero_grad()
            outputs = model(batch_x)
            loss = criterion(outputs, batch_y)
            loss.backward()
            optimizer.step()
            epoch_loss += loss.item()

        avg_loss = epoch_loss / len(loader)
        metric_series.append(MetricRecord(name="train_mse_loss", value=avg_loss, step=epoch))

    metrics["final_mse"] = avg_loss
    parameterset_description = "Current U/V/W 기반 시계열 예측 모델"
    summary_path = "model_summary.txt"
    with open(summary_path, "w") as f:
        f.write(f"Model trained on {len(df)} rows.\\nFinal Loss: {avg_loss}")
    artifact_files.append(ArtifactFile(local_path=summary_path))

    return ExperimentLogBundle(
        params=params,
        metrics=metrics,
        metric_series=metric_series,
        model_input_sample=model_input_sample,
        model=model,
        output_datasets=output_datasets,
        artifact_files=artifact_files,
        parameterset_description=parameterset_description,
        artifacts_description=artifacts_description,
    )
`,
};

const DEMO_RUN_INSTALLED_LIBRARIES: Library[] = [
  { name: 'pandas', type: 'standard' },
  { name: 'requests', type: 'standard' },
  { name: 'scipy', type: 'standard' },
  { name: 'scikit-learn', type: 'standard' },
  { name: 'torch', type: 'standard' },
  { name: 'numpy', type: 'standard' },
  {
    name: 'test_valid_imports',
    type: 'custom',
    content: DEMO_RUN_WORKBENCH_FILES['test_valid_imports.py'],
  },
  {
    name: 'write_your_model_experiment',
    type: 'custom',
    content: DEMO_RUN_WORKBENCH_FILES['write_your_model_experiment.py'],
  },
];

const DEMO_RUN_WORKBENCH_CODE = `import pandas as pd
import requests
import scipy
import test_valid_imports
import sklearn
import torch
import numpy as np
import write_your_model_experiment

${DEMO_RUN_WORKBENCH_FILES['write_your_model_experiment.py']}`;

/**
 * Iceberg Gold 데이터를 standalone FeatureSet/Dataset으로 변환
 * 실제 iceberg-gold.ts의 _source 데이터를 그대로 사용
 */
const createStandaloneAssets = (): AssetSearchResultItem[] => {
  const assets: AssetSearchResultItem[] = [];

  // Feature_set으로 변환할 3개 테이블 (컬럼 적음 = 정제된 데이터)
  // diagnosis_results: 19개 컬럼
  const featureSetIds = new Set([
    '9c60daac-b28a-4e7f-b183-92202f188a7e', // kmp_ecms_diagnosis_results (19 columns)
    '841509c0-f24c-4f52-b67b-317096b5b68d', // gsp_ecms1_diagnosis_result (19 columns)
    '072d6802-fa44-48fa-8e98-27b0b97d0ea6', // hanwha_impact_diagnosis_results (19 columns)
  ]);

  // Dataset으로 변환할 3개 테이블 (컬럼 많음 = 원본 데이터)
  // feature_preprocessed: 44개 컬럼
  const datasetIds = new Set([
    '989ee883-7ee4-4cbd-9396-1ea9f3a784c4', // gsp_ecms1_feature_preprocessed (44 columns)
    'bfe82951-0e52-4e4c-a9af-af0c9a239b89', // kmp_ecms_feature_preprocessed (44 columns)
    'de411cab-abcd-4712-b662-f94e80ee95ca', // hanwha_impact_feature_preprocessed (44 columns)
  ]);

  GOLD_DATA.forEach((hit, index) => {
    const source = hit._source;
    const isFeatureSet = featureSetIds.has(hit._id);
    const isDataset = datasetIds.has(hit._id);

    if (!isFeatureSet && !isDataset) return;

    const assetId = 400 + index + 1;
    const columns = source.columnNames || [];
    const description =
      source.description
        ?.replace(/<[^>]*>/g, '')
        .replace(/&amp;/g, '&')
        .trim() || undefined;

    const baseAsset = {
      id: assetId,
      title: source.displayName || source.name,
      description,
      path: [],
      isLiked: false,
      creator: 'admin',
      updater: undefined,
      createdAt: new Date(source.updatedAt || Date.now()).toISOString(),
      updatedAt: new Date(source.updatedAt || Date.now()).toISOString(),
      project: undefined,
      experiment: undefined,
      run: undefined,
      parameterSet: undefined,
      artifacts: undefined,
      metrics: undefined,
      experimentTags: undefined,
    };

    if (isFeatureSet) {
      assets.push({
        ...baseAsset,
        type: 'feature_set',
        dataset: undefined,
        featureSet: {
          id: assetId,
          snapshotId: 1,
          tier: source.tier?.tagFQN?.includes('Gold') ? 'gold' : 'gold',
          namespace: source.database?.name || 'gold',
          table: source.name,
          description,
          columnCount: columns.length,
          rowCount: 0,
          columns,
          createdAt: new Date(source.updatedAt || Date.now()).toISOString(),
          topMatchedValue: undefined,
        },
      });
    } else {
      assets.push({
        ...baseAsset,
        type: 'dataset',
        featureSet: undefined,
        dataset: {
          id: assetId,
          snapshotId: 1,
          tier: source.tier?.tagFQN?.includes('Gold') ? 'gold' : 'gold',
          namespace: source.database?.name || 'gold',
          table: source.name,
          description,
          columnCount: columns.length,
          rowCount: 0,
          columns,
          createdAt: new Date(source.updatedAt || Date.now()).toISOString(),
          topMatchedValue: undefined,
        },
      });
    }
  });

  return assets;
};

const STANDALONE_ASSETS = createStandaloneAssets();

// 공통 아티팩트 생성 함수 (단순화 버전 - 디렉토리당 파일 1개)
const generateCommonArtifactFiles = (runId: string, datasetId?: string, featureSetId?: string, finishTime?: number) => {
  const time = finishTime || Date.now();
  return [
    // models 디렉토리
    {
      path: 'models',
      isDir: true,
      fileSize: undefined,
      type: 'directory',
    },
    {
      path: 'models/MLmodel',
      isDir: false,
      fileSize: 855,
      type: 'text',
      content: `artifact_path: model
flavors:
  python_function:
    env:
      conda: conda.yaml
      virtualenv: python_env.yaml
    loader_module: mlflow.sklearn
    model_path: model.pkl
    predict_fn: predict
    python_version: 3.9.18
  sklearn:
    code: null
    pickled_model: model.pkl
    serialization_format: cloudpickle
    sklearn_version: 1.3.2
mlflow_version: 2.9.2
model_size_bytes: 1024000
model_uuid: ${runId}-model-uuid
run_id: ${runId}
utc_time_created: '${new Date(time).toISOString()}'`,
    },
    // configs 디렉토리
    {
      path: 'configs',
      isDir: true,
      fileSize: undefined,
      type: 'directory',
    },
    {
      path: 'configs/hyperparameters.json',
      isDir: false,
      fileSize: 324,
      type: 'json',
      content: {
        learning_rate: 0.001,
        batch_size: 32,
        epochs: 100,
        optimizer: 'adam',
        dropout: 0.2,
        hidden_layers: [128, 64, 32],
        activation: 'relu',
        loss_function: 'categorical_crossentropy',
        metrics: ['accuracy', 'precision', 'recall'],
        training_dataset: datasetId || 'default_dataset',
        feature_set: featureSetId || 'default_features',
        timestamp: new Date(time).toISOString(),
      },
    },
    // logs 디렉토리
    {
      path: 'logs',
      isDir: true,
      fileSize: undefined,
      type: 'directory',
    },
    {
      path: 'logs/training.log',
      isDir: false,
      fileSize: 10240,
      type: 'text',
      content: `[${new Date(time - 300000).toISOString()}] Training started
[${new Date(time - 290000).toISOString()}] Loading dataset: ${datasetId || 'dataset'}
[${new Date(time - 280000).toISOString()}] Dataset loaded: 1000 rows, 50 features
[${new Date(time - 270000).toISOString()}] Loading feature set: ${featureSetId || 'featureset'}
[${new Date(time - 260000).toISOString()}] Feature set loaded successfully
[${new Date(time - 250000).toISOString()}] Initializing model: RandomForestClassifier
[${new Date(time - 240000).toISOString()}] Hyperparameters: lr=0.001, batch=32, epochs=100
[${new Date(time - 230000).toISOString()}] Starting training...
[${new Date(time - 220000).toISOString()}] Epoch 1/100 - loss: 0.8543, acc: 0.621
[${new Date(time - 210000).toISOString()}] Epoch 5/100 - loss: 0.2876, acc: 0.851
[${new Date(time - 200000).toISOString()}] Epoch 10/100 - loss: 0.1298, acc: 0.945
[${new Date(time - 190000).toISOString()}] Early stopping - best val_loss: 0.1567
[${new Date(time - 180000).toISOString()}] Training completed
[${new Date(time - 170000).toISOString()}] Evaluating model...
[${new Date(time - 160000).toISOString()}] Test accuracy: 0.9450
[${new Date(time - 150000).toISOString()}] Test precision: 0.9320
[${new Date(time - 140000).toISOString()}] Test recall: 0.9180
[${new Date(time - 130000).toISOString()}] Test f1_score: 0.9250
[${new Date(time - 120000).toISOString()}] Model saved successfully
[${new Date(time).toISOString()}] Training finished - Total: 5m 0s`,
    },
  ];
};

const createRunWithAssets = (
  id: number,
  name: string,
  projectId: number,
  projectName: string,
  experimentId: number,
  experimentName: string,
  status: string,
  daysAgo: number,
  featureSetAsset?: AssetSearchResultItem,
  datasetAsset?: AssetSearchResultItem
): MockRunWithAssets => {
  const startTime = new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000 - 1000 * 60 * 2);

  // CREATED 상태(준비중)인 런은 아직 실행되지 않아 parameterSet, artifacts, metrics, experimentTags 없음
  if (status === 'Execution_Ready') {
    return {
      id: String(id),
      name,
      projectId,
      projectName,
      experimentId,
      experimentName,
      status,
      startTime: startTime.toISOString(),
      counts: {
        featureSet: 0,
        dataset: 0,
        parameterSet: 0,
        artifact: 0,
      },
      featureSetAsset: undefined,
      datasetAsset: undefined,
      parameterSet: undefined,
      artifacts: undefined,
      metrics: undefined,
      experimentTags: undefined,
    };
  }

  // 실행된 런만 아래 데이터 생성
  const parameterSetId = id + 1000;
  const artifactId = id + 2000;

  const parameterSet = {
    id: parameterSetId,
    runId: id,
    hyperparameters: {
      data_source: datasetAsset
        ? `gold.${datasetAsset.dataset?.namespace || 'experiments'}.${datasetAsset.dataset?.table}`
        : featureSetAsset
          ? `gold.${featureSetAsset.featureSet?.namespace || 'experiments'}.${featureSetAsset.featureSet?.table}`
          : 'gold.features.default',
      random_seed: 42,
      random_state: 42,
      columns_loaded: (featureSetAsset?.featureSet?.columns || datasetAsset?.dataset?.columns || ['col1'])
        .slice(0, 4)
        .join(','),
      experiment_mode: featureSetAsset && datasetAsset ? 'full' : featureSetAsset ? 'feature_only' : 'data_only',
      numeric_feature_columns: (featureSetAsset?.featureSet?.columns || datasetAsset?.dataset?.columns || [])
        .slice(1, 4)
        .join(','),
    },
    description: undefined,
    topMatchedValue: undefined,
  };

  // 공통 아티팩트 사용 (간소화)
  const commonFiles = generateCommonArtifactFiles(
    String(id),
    datasetAsset?.dataset?.table,
    featureSetAsset?.featureSet?.table,
    startTime.getTime()
  );

  const artifacts = {
    id: artifactId,
    count: commonFiles.length,
    description: undefined,
    topMatchedValue: undefined,
    files: commonFiles,
  };

  const metrics = {
    std_current_u: 0.149071,
    std_current_v: 0.149071,
    std_current_w: 0.149071,
    data_row_count: Math.floor(Math.random() * 1000) + 100,
    mean_current_u: Math.random() * 2,
    mean_current_v: Math.random() * -3,
    mean_current_w: Math.random() * 2,
    numeric_feature_count: 3,
    'system_end.cpu_percent': Math.random() * 5,
    'system_start.cpu_percent': Math.random() * 5,
    'system_end.memory_percent': 30 + Math.random() * 10,
    'system_start.memory_percent': 30 + Math.random() * 10,
    pca_explained_variance_ratio_0: 1,
    pca_explained_variance_ratio_1: 0,
    'system_end.memory_available_gb': 340 + Math.random() * 20,
    pca_explained_variance_ratio_sum: 1,
    'system_start.memory_available_gb': 340 + Math.random() * 20,
  };

  const experimentTags: Record<string, string> = {
    example: 'basic_experiment',
    cyclone_source_experiment_id: String(experimentId),
  };

  return {
    id: String(id),
    name,
    projectId,
    projectName,
    experimentId,
    experimentName,
    status,
    startTime: startTime.toISOString(),
    counts: {
      featureSet: featureSetAsset ? 1 : 0,
      dataset: datasetAsset ? 1 : 0,
      parameterSet: 1,
      artifact: artifacts.count,
    },
    featureSetAsset,
    datasetAsset,
    parameterSet,
    artifacts,
    metrics,
    experimentTags,
  };
};

/**
 * 런 이름 생성 헬퍼 - 시퀀스 관리
 */
const createRunNameWithSequence = (baseName: string, sequenceMap: Map<string, number>): string => {
  const currentSeq = sequenceMap.get(baseName) || 0;
  const nextSeq = currentSeq + 1;
  sequenceMap.set(baseName, nextSeq);
  return `${baseName} #${nextSeq}`;
};

/**
 * Asset의 description을 기반으로 의미있는 런 베이스 이름 생성
 */
const generateRunBaseName = (
  featureSetAsset?: AssetSearchResultItem,
  datasetAsset?: AssetSearchResultItem,
  experimentType?: string
): string => {
  // Experiment 타입별 기본 런 이름
  const baseNames = {
    diagnostics: ['ecms-health-check', 'vibration-analysis', 'equipment-monitoring'],
    features: ['feature-extraction', 'signal-processing', 'data-transformation'],
    anomaly: ['anomaly-detection', 'fault-prediction', 'outlier-analysis'],
  };

  if (experimentType && baseNames[experimentType as keyof typeof baseNames]) {
    const names = baseNames[experimentType as keyof typeof baseNames];
    return names[Math.floor(Math.random() * names.length)];
  }

  // Asset 기반 이름 생성
  if (featureSetAsset && datasetAsset) {
    return 'full-pipeline-training';
  } else if (featureSetAsset) {
    const title = featureSetAsset.title.toLowerCase();
    if (title.includes('kmp')) return 'kmp-diagnostics';
    if (title.includes('gsp')) return 'gsp-diagnostics';
    if (title.includes('hanwha')) return 'hanwha-diagnostics';
    return 'equipment-diagnostics';
  } else if (datasetAsset) {
    const title = datasetAsset.title.toLowerCase();
    if (title.includes('kmp')) return 'kmp-feature-prep';
    if (title.includes('gsp')) return 'gsp-feature-prep';
    if (title.includes('hanwha')) return 'hanwha-feature-prep';
    return 'feature-preprocessing';
  }

  return 'baseline-setup';
};

/**
 * Standalone assets를 기반으로 Run 생성
 * 3개 실험: 5개, 3개, 4개 런 + 1개 준비중 런
 */
const createRunsFromAssets = (standaloneAssets: AssetSearchResultItem[]): MockRunWithAssets[] => {
  const runs: MockRunWithAssets[] = [];
  let runIdCounter = 280;
  const sequenceMap = new Map<string, number>();

  const featureSets = standaloneAssets.filter((a) => a.type === 'feature_set');
  const datasets = standaloneAssets.filter((a) => a.type === 'dataset');

  const projectId = 9;
  const projectName = 'Manufacturing Analytics';

  // === Experiment 1: Equipment Diagnostics (5개 런) ===
  const exp1Id = 101;
  const exp1Name = 'Equipment Diagnostics';

  // Run 1-3: FeatureSet + Dataset 조합 (진단 결과 + 원본 feature)
  for (let i = 0; i < 3; i++) {
    runIdCounter++;
    const featureSet = featureSets[i];
    const dataset = datasets[i];
    const baseName = generateRunBaseName(featureSet, dataset, 'diagnostics');
    const runName = createRunNameWithSequence(baseName, sequenceMap);

    runs.push(
      createRunWithAssets(
        runIdCounter,
        runName,
        projectId,
        projectName,
        exp1Id,
        exp1Name,
        'Execution_Succeeded',
        i,
        featureSet,
        dataset
      )
    );
  }

  // Run 4-5: FeatureSet만 (진단 결과만)
  for (let i = 0; i < 2; i++) {
    runIdCounter++;
    const featureSet = featureSets[i];
    const baseName = generateRunBaseName(featureSet, undefined, 'diagnostics');
    const runName = createRunNameWithSequence(baseName, sequenceMap);

    runs.push(
      createRunWithAssets(
        runIdCounter,
        runName,
        projectId,
        projectName,
        exp1Id,
        exp1Name,
        'Execution_Succeeded',
        3 + i,
        featureSet,
        undefined
      )
    );
  }

  // === Experiment 2: Feature Engineering (3개 런) ===
  const exp2Id = 102;
  const exp2Name = 'Feature Engineering Pipeline';

  // Run 6-8: Dataset만 (원본 feature 데이터)
  for (let i = 0; i < 3; i++) {
    runIdCounter++;
    const dataset = datasets[i];
    const baseName = generateRunBaseName(undefined, dataset, 'features');
    const runName = createRunNameWithSequence(baseName, sequenceMap);

    runs.push(
      createRunWithAssets(
        runIdCounter,
        runName,
        projectId,
        projectName,
        exp2Id,
        exp2Name,
        'Execution_Succeeded',
        5 + i,
        undefined,
        dataset
      )
    );
  }

  // === Experiment 3: Anomaly Detection (4개 런) ===
  const exp3Id = 103;
  const exp3Name = 'Anomaly Detection Model';

  // Run 9-10: FeatureSet + Dataset
  for (let i = 0; i < 2; i++) {
    runIdCounter++;
    const featureSet = featureSets[(i + 1) % featureSets.length];
    const dataset = datasets[(i + 1) % datasets.length];
    const baseName = generateRunBaseName(featureSet, dataset, 'anomaly');
    const runName = createRunNameWithSequence(baseName, sequenceMap);

    runs.push(
      createRunWithAssets(
        runIdCounter,
        runName,
        projectId,
        projectName,
        exp3Id,
        exp3Name,
        'Execution_Succeeded',
        8 + i,
        featureSet,
        dataset
      )
    );
  }

  // Run 11-12: FeatureSet만
  for (let i = 0; i < 2; i++) {
    runIdCounter++;
    const featureSet = featureSets[(i + 2) % featureSets.length];
    const baseName = generateRunBaseName(featureSet, undefined, 'anomaly');
    const runName = createRunNameWithSequence(baseName, sequenceMap);

    runs.push(
      createRunWithAssets(
        runIdCounter,
        runName,
        projectId,
        projectName,
        exp3Id,
        exp3Name,
        'Execution_Succeeded',
        10 + i,
        featureSet,
        undefined
      )
    );
  }

  // === Experiment 4: Baseline Setup (1개 런 - 준비중) ===
  const exp4Id = 104;
  const exp4Name = 'Baseline Model Training';

  runIdCounter++;
  const baseName = generateRunBaseName(undefined, undefined);
  const runName = createRunNameWithSequence(baseName, sequenceMap);

  runs.push(
    createRunWithAssets(
      runIdCounter,
      runName,
      projectId,
      projectName,
      exp4Id,
      exp4Name,
      'Execution_Ready',
      12,
      undefined,
      undefined
    )
  );

  return runs;
};

/**
 * GOLD 기반 dataset/feature_set(단독 검색 타입)이 `/experiments/...` 형식의 URL을 사용하도록
 * 해당 자산이 최초로 등장하는 런(가장 작은 run id)의 프로젝트/실험/런 메타데이터를 붙입니다.
 * (객체는 런 mock과 동일한 참조입니다.)
 */
const attachPrimaryRunContextToStandaloneGoldAssets = (runs: MockRunWithAssets[]): void => {
  const sortedRuns = [...runs].sort((a, b) => Number(a.id) - Number(b.id));
  const linkedAssetIds = new Set<number>();

  const attach = (asset: AssetSearchResultItem | undefined, run: MockRunWithAssets) => {
    if (!asset || linkedAssetIds.has(asset.id)) return;

    linkedAssetIds.add(asset.id);

    const endTime =
      run.status === 'Execution_Succeeded'
        ? new Date(new Date(run.startTime).getTime() + 1000 * 60 * 20).toISOString()
        : undefined;

    asset.path = [
      { type: 'project', id: run.projectId, label: run.projectName, to: `/experiments/${run.projectId}` },
      {
        type: 'experiment',
        id: run.experimentId,
        label: run.experimentName,
        to: `/experiments/${run.projectId}/${run.experimentId}`,
      },
      {
        type: 'run',
        id: Number(run.id),
        label: run.name,
        to: `/experiments/${run.projectId}/${run.experimentId}/${run.id}?assetType=run`,
      },
    ];
    asset.project = {
      id: run.projectId,
      projectName: run.projectName,
    };
    asset.experiment = {
      id: run.experimentId,
      experimentName: run.experimentName,
      description: undefined,
      runsCount: 2,
      lastRuntime: run.startTime,
    };
    asset.run = {
      id: Number(run.id),
      runName: run.name,
      description: undefined,
      startTime: run.startTime,
      endTime,
      status: run.status,
    } as NonNullable<AssetSearchResultItem['run']>;
  };

  for (const run of sortedRuns) {
    attach(run.featureSetAsset, run);
    attach(run.datasetAsset, run);
  }
};

const INITIAL_MOCK_RUNS: MockRunWithAssets[] = createRunsFromAssets(STANDALONE_ASSETS);

attachPrimaryRunContextToStandaloneGoldAssets(INITIAL_MOCK_RUNS);

let mockRunStore: MockRunWithAssets[] = [...INITIAL_MOCK_RUNS];

// localStorage 키
const PROJECTS_STORAGE_KEY = 'cyclone_custom_projects';
const EXPERIMENTS_STORAGE_KEY = 'cyclone_custom_experiments';
const RUNS_STORAGE_KEY = 'cyclone_custom_runs';
const EXECUTIONS_STORAGE_KEY = 'cyclone_custom_run_executions';

// 커스텀 프로젝트 타입
type CustomProject = {
  id: number;
  name: string;
  description: string;
  createdAt: number;
};

// 커스텀 실험 타입
type CustomExperiment = {
  id: number;
  projectId: number;
  name: string;
  description: string;
  createdAt: number;
  updatedAt: number;
  creator: string;
  updater: string;
};

type ExperimentOverride = {
  name?: string;
  description?: string | null;
  updatedAt: number;
};

// 커스텀 런 타입
type CustomRun = {
  id: string;
  name: string;
  projectId: number;
  projectName: string;
  experimentId: number;
  experimentName: string;
  status: 'Execution_Ready' | 'Execution_Running' | 'Execution_Succeeded' | 'Execution_Failed' | 'Execution_Canceled';
  startTime: string;
  endTime?: string;
  createdAt: number;
  updatedAt: number;
  // 입력 자산
  datasetId?: string;
  featureSetId?: string;
  datasetSnapshotId?: number | null;
  featureSetSnapshotId?: number | null;
  parameterSetId?: number | string | null;
  // 컴퓨팅 리소스
  computingResource: string;
  // 실행 후 추가되는 정보
  metrics?: Record<string, number>;
  parameterSet?: {
    id: number;
    runId: number;
    hyperparameters: Record<string, string | number | boolean>;
    description?: string;
  };
  artifacts?: Array<{
    path: string;
    isDir: boolean;
    fileSize?: number;
    type?: string;
    typeId?: string;
    content?: string | object;
  }>;
  model?: MockRunWithAssets['model'];
};

type DemoExecutionRecord = ExecutionResponse & {
  installedLibraries?: Library[];
};

// localStorage에서 커스텀 프로젝트 불러오기
const loadCustomProjects = (): CustomProject[] => {
  try {
    const stored = localStorage.getItem(PROJECTS_STORAGE_KEY);
    const projects = stored ? JSON.parse(stored) : [];
    return projects;
  } catch (error) {
    console.error('[experimentMockStore] localStorage 로드 실패:', error);
    return [];
  }
};

// localStorage에 커스텀 프로젝트 저장
const saveCustomProjects = (projects: CustomProject[]): void => {
  try {
    localStorage.setItem(PROJECTS_STORAGE_KEY, JSON.stringify(projects));
  } catch (error) {
    console.error('[experimentMockStore] localStorage 저장 실패:', error);
  }
};

// localStorage에서 커스텀 실험 불러오기
const loadCustomExperiments = (): CustomExperiment[] => {
  try {
    const stored = localStorage.getItem(EXPERIMENTS_STORAGE_KEY);
    const experiments = stored ? JSON.parse(stored) : [];
    return experiments;
  } catch (error) {
    console.error('[experimentMockStore] localStorage 실험 로드 실패:', error);
    return [];
  }
};

// localStorage에 커스텀 실험 저장
const saveCustomExperiments = (experiments: CustomExperiment[]): void => {
  try {
    localStorage.setItem(EXPERIMENTS_STORAGE_KEY, JSON.stringify(experiments));
  } catch (error) {
    console.error('[experimentMockStore] localStorage 실험 저장 실패:', error);
  }
};

// localStorage에서 커스텀 런 불러오기
export const loadCustomRuns = (): CustomRun[] => {
  try {
    const stored = localStorage.getItem(RUNS_STORAGE_KEY);
    const runs = stored ? JSON.parse(stored) : [];
    return runs;
  } catch (error) {
    console.error('[experimentMockStore] localStorage 런 로드 실패:', error);
    return [];
  }
};

// localStorage에 커스텀 런 저장
const saveCustomRuns = (runs: CustomRun[]): void => {
  try {
    localStorage.setItem(RUNS_STORAGE_KEY, JSON.stringify(runs));
  } catch (error) {
    console.error('[experimentMockStore] localStorage 런 저장 실패:', error);
  }
};

const loadDemoExecutions = (): DemoExecutionRecord[] => {
  try {
    const stored = localStorage.getItem(EXECUTIONS_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('[experimentMockStore] localStorage 실행 이력 로드 실패:', error);
    return [];
  }
};

const saveDemoExecutions = (executions: DemoExecutionRecord[]): void => {
  try {
    localStorage.setItem(EXECUTIONS_STORAGE_KEY, JSON.stringify(executions));
  } catch (error) {
    console.error('[experimentMockStore] localStorage 실행 이력 저장 실패:', error);
  }
};

// 커스텀 프로젝트 저장소
let customProjects: CustomProject[] = loadCustomProjects();

// 커스텀 실험 저장소
let customExperiments: CustomExperiment[] = loadCustomExperiments();

// 커스텀 런 저장소
let customRuns: CustomRun[] = loadCustomRuns();

let demoExecutions: DemoExecutionRecord[] = loadDemoExecutions();

const experimentOverrides = new Map<number, ExperimentOverride>();

// 다음 프로젝트 ID 계산 (기존 프로젝트들과 겹치지 않도록)
const getNextProjectId = (): number => {
  const existingIds = mockRunStore.map((run) => run.projectId);
  const customIds = customProjects.map((p) => p.id);
  const allIds = [...existingIds, ...customIds];
  return allIds.length > 0 ? Math.max(...allIds) + 1 : 1000;
};

// 다음 실험 ID 계산 (기존 실험들과 겹치지 않도록)
const getNextExperimentId = (): number => {
  const existingIds = mockRunStore.map((run) => run.experimentId);
  const customIds = customExperiments.map((e) => e.id);
  const allIds = [...existingIds, ...customIds];
  return allIds.length > 0 ? Math.max(...allIds) + 1 : 2000;
};

// 다음 런 ID 계산 (기존 런들과 겹치지 않도록)
const getNextRunId = (): string => {
  const existingIds = mockRunStore.map((run) => parseInt(run.id, 10)).filter((id) => !isNaN(id));
  const customIds = customRuns.map((run) => parseInt(run.id, 10)).filter((id) => !isNaN(id));
  const allIds = [...existingIds, ...customIds];
  const maxId = allIds.length > 0 ? Math.max(...allIds) : 3000;
  return String(maxId + 1);
};

const toNullableNumber = (value: unknown): number | null => {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const findDatasetAsset = (
  run: Pick<CustomRun, 'datasetId' | 'datasetSnapshotId'>
): AssetSearchResultItem | undefined => {
  if (!run.datasetId && run.datasetSnapshotId == null) return undefined;

  return STANDALONE_ASSETS.find((asset) => {
    if (!asset.dataset) return false;

    return (
      asset.dataset.table === run.datasetId ||
      String(asset.id) === run.datasetId ||
      String(asset.dataset.snapshotId) === String(run.datasetSnapshotId) ||
      String(asset.dataset.id) === String(run.datasetSnapshotId)
    );
  });
};

const findFeatureSetAsset = (
  run: Pick<CustomRun, 'featureSetId' | 'featureSetSnapshotId'>
): AssetSearchResultItem | undefined => {
  if (!run.featureSetId && run.featureSetSnapshotId == null) return undefined;

  return STANDALONE_ASSETS.find((asset) => {
    if (!asset.featureSet) return false;

    return (
      asset.featureSet.table === run.featureSetId ||
      String(asset.id) === run.featureSetId ||
      String(asset.featureSet.snapshotId) === String(run.featureSetSnapshotId) ||
      String(asset.featureSet.id) === String(run.featureSetSnapshotId)
    );
  });
};

const resolveParameterSet = (
  parameterSetId: CustomRun['parameterSetId'],
  allRuns: Array<MockRunWithAssets | CustomRun>
): MockRunWithAssets['parameterSet'] | undefined => {
  if (parameterSetId === undefined || parameterSetId === null || parameterSetId === '') return undefined;

  const sourceRun = allRuns.find((run) => String(run.parameterSet?.id) === String(parameterSetId));
  return sourceRun?.parameterSet;
};

const toMockRunWithAssets = (
  customRun: CustomRun,
  allCustomRuns: CustomRun[] = loadCustomRuns()
): MockRunWithAssets => {
  const datasetAsset = findDatasetAsset(customRun);
  const featureSetAsset = findFeatureSetAsset(customRun);
  const parameterSet =
    customRun.parameterSet ?? resolveParameterSet(customRun.parameterSetId, [...mockRunStore, ...allCustomRuns]);

  return {
    id: customRun.id,
    name: customRun.name,
    status: customRun.status,
    startTime: customRun.startTime,
    projectId: customRun.projectId,
    projectName: customRun.projectName,
    experimentId: customRun.experimentId,
    experimentName: customRun.experimentName,
    counts: {
      featureSet: featureSetAsset ? 1 : 0,
      dataset: datasetAsset ? 1 : 0,
      parameterSet: parameterSet ? 1 : 0,
      artifact: customRun.artifacts ? customRun.artifacts.length : 0,
    },
    datasetAsset,
    featureSetAsset,
    parameterSet,
    artifacts: customRun.artifacts
      ? {
          id: parseInt(customRun.id, 10) * 10,
          count: customRun.artifacts.length,
          runUuid: customRun.id,
          description: undefined,
          files: customRun.artifacts,
        }
      : undefined,
    metrics: customRun.metrics,
    experimentTags: {},
    model: customRun.model,
  };
};

const findRunWithAssetsById = (runId: string | number): MockRunWithAssets | undefined => {
  const targetRunId = String(runId);
  const latestCustomRuns = loadCustomRuns();
  const customRunsAsMock = latestCustomRuns.map((customRun) => toMockRunWithAssets(customRun, latestCustomRuns));

  return [...mockRunStore, ...customRunsAsMock].find((run) => String(run.id) === targetRunId);
};

const getRunEndTime = (run: MockRunWithAssets): string | null => {
  if (
    run.status !== 'Execution_Succeeded' &&
    run.status !== 'Execution_Failed' &&
    run.status !== 'Execution_Canceled'
  ) {
    return null;
  }

  const fallbackEndedAt = new Date(new Date(run.startTime).getTime() + 1000 * 60 * 20).toISOString();
  return 'endTime' in run && typeof run.endTime === 'string' ? run.endTime : fallbackEndedAt;
};

const toExecutionStatus = (runStatus: string): string => {
  switch (runStatus) {
    case 'Execution_Succeeded':
      return 'execution_succeeded';
    case 'Execution_Failed':
      return 'execution_failed';
    case 'Execution_Canceled':
      return 'execution_canceled';
    case 'Execution_Running':
      return 'execution_running';
    default:
      return 'execution_queued';
  }
};

const EXECUTION_QUEUED_MS = 3_000;
const EXECUTION_SUCCESS_MS = 25_000;
const TERMINAL_EXECUTION_STATUSES = new Set<ExecutionStatus>([
  'execution_succeeded',
  'execution_failed',
  'execution_canceled',
]);

const createDemoRunModel = (runId: string | number): NonNullable<MockRunWithAssets['model']> => ({
  modelId: `demo-model-${runId}`,
  modelFormat: 'pytorch',
  servingConfig: {
    backend: 'pytorch',
    convertToOnnx: false,
  },
});

const getDemoRunModel = (run: MockRunWithAssets): MockRunWithAssets['model'] => {
  if (run.model) return run.model;

  if (run.status === 'Execution_Succeeded' || run.metrics || run.artifacts) {
    return createDemoRunModel(run.id);
  }

  return null;
};

const createDemoExecutionMetrics = (runId: string | number): Record<string, number> => {
  const seed = Number(runId);
  const offset = Number.isFinite(seed) ? (seed % 17) / 1000 : 0.004;

  return {
    accuracy: Number((0.935 + offset).toFixed(6)),
    loss: Number((0.122 - offset / 2).toFixed(6)),
    f1_score: Number((0.918 + offset).toFixed(6)),
    precision: Number((0.927 + offset).toFixed(6)),
    recall: Number((0.91 + offset).toFixed(6)),
    train_mse_loss: Number((0.084 - offset / 3).toFixed(6)),
  };
};

const createDemoExecutionParameterSet = (runId: string | number): NonNullable<CustomRun['parameterSet']> => ({
  id: Number(runId) * 100,
  runId: Number(runId),
  hyperparameters: {
    learning_rate: 0.001,
    batch_size: 64,
    epochs: 20,
    optimizer: 'adam',
    hidden_dim: 32,
  },
  description: 'Demo execution parameter set',
});

const getNextExecutionId = (): number => {
  const existingIds = demoExecutions.map((execution) => Number(execution.id)).filter(Number.isFinite);
  return existingIds.length > 0 ? Math.max(...existingIds) + 1 : 1;
};

const cloneExecution = (execution: DemoExecutionRecord): DemoExecutionRecord => ({
  ...execution,
  files: execution.files ? { ...execution.files } : undefined,
  resourceSpec: { ...execution.resourceSpec },
  installedLibraries: execution.installedLibraries?.map((library) => ({ ...library })),
});

const mapExecutionStatusToCustomRunStatus = (status: ExecutionStatus): CustomRun['status'] => {
  switch (status) {
    case 'execution_succeeded':
      return 'Execution_Succeeded';
    case 'execution_failed':
      return 'Execution_Failed';
    case 'execution_canceled':
      return 'Execution_Canceled';
    case 'execution_running':
    case 'execution_queued':
      return 'Execution_Running';
  }
};

const applyExecutionToRun = (execution: DemoExecutionRecord): void => {
  const runId = String(execution.runId);
  const now = Date.now();
  const nextRunStatus = mapExecutionStatusToCustomRunStatus(execution.status);

  customRuns = loadCustomRuns();
  const customRun = customRuns.find((run) => run.id === runId);

  if (customRun) {
    customRun.status = nextRunStatus;
    customRun.startTime = execution.startedAt || execution.queuedAt;
    customRun.endTime = execution.endedAt || undefined;
    customRun.updatedAt = now;

    if (execution.status === 'execution_succeeded') {
      const finishTime = execution.endedAt ? new Date(execution.endedAt).getTime() : now;
      customRun.metrics = customRun.metrics ?? createDemoExecutionMetrics(runId);
      customRun.parameterSet = customRun.parameterSet ?? createDemoExecutionParameterSet(runId);
      customRun.artifacts =
        customRun.artifacts ??
        generateCommonArtifactFiles(runId, customRun.datasetId, customRun.featureSetId, finishTime);
      customRun.model = customRun.model ?? createDemoRunModel(runId);
    }

    saveCustomRuns(customRuns);
    return;
  }

  const fixtureRun = mockRunStore.find((run) => String(run.id) === runId);

  if (!fixtureRun) return;

  fixtureRun.status = nextRunStatus;

  if (execution.status === 'execution_succeeded') {
    const finishTime = execution.endedAt ? new Date(execution.endedAt).getTime() : now;
    fixtureRun.metrics = fixtureRun.metrics ?? createDemoExecutionMetrics(runId);
    fixtureRun.parameterSet = fixtureRun.parameterSet ?? {
      ...createDemoExecutionParameterSet(runId),
      topMatchedValue: undefined,
    };
    fixtureRun.artifacts = fixtureRun.artifacts ?? {
      id: Number(runId) * 10,
      count: generateCommonArtifactFiles(runId, undefined, undefined, finishTime).length,
      runUuid: runId,
      files: generateCommonArtifactFiles(runId, undefined, undefined, finishTime),
    };
    fixtureRun.model = fixtureRun.model ?? createDemoRunModel(runId);
  }
};

const refreshDemoExecution = (execution: DemoExecutionRecord, now: number = Date.now()): DemoExecutionRecord => {
  if (TERMINAL_EXECUTION_STATUSES.has(execution.status)) {
    applyExecutionToRun(execution);
    return execution;
  }

  const queuedAtMs = new Date(execution.queuedAt || execution.createdAt).getTime();
  const elapsed = now - queuedAtMs;

  if (elapsed >= EXECUTION_SUCCESS_MS) {
    execution.status = 'execution_succeeded';
    execution.startedAt = execution.startedAt ?? new Date(queuedAtMs + EXECUTION_QUEUED_MS).toISOString();
    execution.endedAt = new Date(queuedAtMs + EXECUTION_SUCCESS_MS).toISOString();
    execution.errorMessage = null;
    execution.error_message = null;
  } else if (elapsed >= EXECUTION_QUEUED_MS) {
    execution.status = 'execution_running';
    execution.startedAt = execution.startedAt ?? new Date(queuedAtMs + EXECUTION_QUEUED_MS).toISOString();
    execution.endedAt = null;
  } else {
    execution.status = 'execution_queued';
    execution.startedAt = null;
    execution.endedAt = null;
  }

  applyExecutionToRun(execution);
  saveDemoExecutions(demoExecutions);
  return execution;
};

const getLatestDemoExecutionForRun = (runId: string | number): DemoExecutionRecord | undefined => {
  demoExecutions = loadDemoExecutions();
  const targetRunId = String(runId);
  const execution = demoExecutions
    .filter((item) => String(item.runId) === targetRunId)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

  return execution ? refreshDemoExecution(execution) : undefined;
};

const toMetricHistory = (metrics: Record<string, number>): Record<string, Array<{ step: number; value: number }>> => {
  return Object.fromEntries(
    Object.entries(metrics).map(([name, value]) => {
      const firstValue = Number((value * 0.72).toFixed(6));
      const secondValue = Number((value * 0.9).toFixed(6));

      return [
        name,
        [
          { step: 0, value: firstValue },
          { step: 1, value: secondValue },
          { step: 2, value },
        ],
      ];
    })
  );
};

const createDemoWorkbenchExecutionPayload = (runId: string | number = 1020) => {
  const numericRunId = Number(runId);

  return {
    id: 641,
    runId: Number.isFinite(numericRunId) ? numericRunId : 1020,
    code: DEMO_RUN_WORKBENCH_CODE,
    files: { ...DEMO_RUN_WORKBENCH_FILES },
    status: 'execution_failed',
    k8SJobName: `rw-run-${runId}-exec-641`,
    k8SNamespace: 'dev-remote-workspace',
    configmapName: `rw-run-${runId}-exec-641`,
    resourceSpec: {
      cpu: '4',
      gpu: '0',
      memory: '16Gi',
    },
    queuedAt: '2026-05-20T05:22:32.449409Z',
    startedAt: '2026-05-20T05:22:36.485448Z',
    endedAt: '2026-05-20T05:23:03.228642Z',
    errorMessage: 'K8s Job 실패',
    error_message: 'K8s Job 실패',
    sourceRunId: String(runId),
    source_run_id: String(runId),
    createdAt: '2026-05-20T05:22:32.449417Z',
    installedLibraries: DEMO_RUN_INSTALLED_LIBRARIES.map((library) => ({ ...library })),
  };
};

// 프로젝트 생성 함수
export const createProjectMock = (name: string, description: string): CustomProject => {
  const newProject: CustomProject = {
    id: getNextProjectId(),
    name,
    description,
    createdAt: Date.now(),
  };

  customProjects.push(newProject);
  saveCustomProjects(customProjects);

  return newProject;
};

// 실험 생성 함수
export const createExperimentMock = (projectId: number, name: string, description: string): CustomExperiment => {
  const now = Date.now();
  const newExperiment: CustomExperiment = {
    id: getNextExperimentId(),
    projectId,
    name,
    description,
    createdAt: now,
    updatedAt: now,
    creator: 'admin',
    updater: 'admin',
  };

  customExperiments.push(newExperiment);
  saveCustomExperiments(customExperiments);

  return newExperiment;
};

export const updateExperimentMock = (
  experimentId: number,
  data: { experimentName?: string | null; description?: string | null }
): CustomExperiment | null => {
  const now = Date.now();
  const customExperiment = customExperiments.find((experiment) => experiment.id === experimentId);

  if (customExperiment) {
    customExperiment.name = data.experimentName?.trim() || customExperiment.name;
    customExperiment.description = data.description ?? customExperiment.description;
    customExperiment.updatedAt = now;
    customExperiment.updater = 'admin';
    saveCustomExperiments(customExperiments);
    return customExperiment;
  }

  const relatedRuns = mockRunStore.filter((run) => run.experimentId === experimentId);
  if (relatedRuns.length === 0) {
    return null;
  }

  const nextName = data.experimentName?.trim();
  if (nextName) {
    relatedRuns.forEach((run) => {
      run.experimentName = nextName;
    });
  }

  experimentOverrides.set(experimentId, {
    name: nextName,
    description: data.description ?? null,
    updatedAt: now,
  });

  const firstRun = relatedRuns[0];
  return {
    id: experimentId,
    projectId: firstRun.projectId,
    name: nextName || firstRun.experimentName,
    description: data.description ?? '',
    createdAt: new Date(firstRun.startTime).getTime(),
    updatedAt: now,
    creator: 'system',
    updater: 'admin',
  };
};

// 런 생성 함수
export const createRunMock = (params: {
  name: string;
  projectId: number;
  projectName: string;
  experimentId: number;
  experimentName: string;
  datasetId?: string;
  featureSetId?: string;
  datasetSnapshotId?: number | null;
  featureSetSnapshotId?: number | null;
  parameterSetId?: number | string | null;
  computingResource: string;
}): CustomRun => {
  const now = Date.now();
  const nowISO = new Date(now).toISOString();

  const newRun: CustomRun = {
    id: getNextRunId(),
    name: params.name,
    projectId: params.projectId,
    projectName: params.projectName,
    experimentId: params.experimentId,
    experimentName: params.experimentName,
    status: 'Execution_Ready',
    startTime: nowISO,
    createdAt: now,
    updatedAt: now,
    datasetId: params.datasetId,
    featureSetId: params.featureSetId,
    datasetSnapshotId: params.datasetSnapshotId,
    featureSetSnapshotId: params.featureSetSnapshotId,
    parameterSetId: params.parameterSetId,
    computingResource: params.computingResource,
  };

  customRuns.push(newRun);
  saveCustomRuns(customRuns);

  return newRun;
};

export const startRunExecutionMock = (runId: string | number, request?: ExecuteRequest): DemoExecutionRecord | null => {
  const run = findRunWithAssetsById(runId);

  if (!run) {
    console.error('[experimentMockStore] 런을 찾을 수 없음:', runId);
    return null;
  }

  const numericRunId = Number(runId);
  const now = new Date();
  const execution: DemoExecutionRecord = {
    id: getNextExecutionId(),
    runId: Number.isFinite(numericRunId) ? numericRunId : Number(run.id),
    code: request?.build?.code || DEMO_RUN_WORKBENCH_CODE,
    files:
      request?.build?.files && Object.keys(request.build.files).length > 0
        ? { ...request.build.files }
        : { ...DEMO_RUN_WORKBENCH_FILES },
    status: 'execution_queued',
    k8SJobName: `demo-run-${runId}-exec-${Date.now()}`,
    k8SNamespace: 'cyclone-demo',
    configmapName: `demo-run-${runId}-config`,
    resourceSpec: request?.resourceSpec ?? {
      cpu: '2',
      memory: '4Gi',
      gpu: '0',
    },
    queuedAt: now.toISOString(),
    startedAt: null,
    endedAt: null,
    errorMessage: null,
    error_message: null,
    sourceRunId: String(runId),
    source_run_id: String(runId),
    createdAt: now.toISOString(),
    installedLibraries: DEMO_RUN_INSTALLED_LIBRARIES.map((library) => ({ ...library })),
  };

  demoExecutions = loadDemoExecutions();
  demoExecutions.push(execution);
  saveDemoExecutions(demoExecutions);
  applyExecutionToRun(execution);

  return cloneExecution(execution);
};

// 이전 demo route(`/runs/:id/execute`) 호환용 런 실행 함수
export const executeRunMock = (runId: string): CustomRun | null => {
  const execution = startRunExecutionMock(runId);

  if (!execution) {
    return null;
  }

  customRuns = loadCustomRuns();
  return (customRuns.find((run) => run.id === String(runId)) ??
    findRunWithAssetsById(runId) ??
    null) as CustomRun | null;
};

export const getRunExecutionMock = (
  runId: string | number,
  executionId: string | number
): DemoExecutionRecord | null => {
  demoExecutions = loadDemoExecutions();
  const execution = demoExecutions.find(
    (item) => String(item.runId) === String(runId) && String(item.id) === String(executionId)
  );

  if (!execution) {
    return null;
  }

  return cloneExecution(refreshDemoExecution(execution));
};

export const cancelRunExecutionMock = (
  runId: string | number,
  executionId: string | number
): DemoExecutionRecord | null => {
  demoExecutions = loadDemoExecutions();
  const execution = demoExecutions.find(
    (item) => String(item.runId) === String(runId) && String(item.id) === String(executionId)
  );

  if (!execution) {
    return null;
  }

  if (!TERMINAL_EXECUTION_STATUSES.has(execution.status)) {
    const now = new Date().toISOString();
    execution.status = 'execution_canceled';
    execution.endedAt = now;
    execution.errorMessage = null;
    execution.error_message = null;
  }

  applyExecutionToRun(execution);
  saveDemoExecutions(demoExecutions);

  return cloneExecution(execution);
};

export const updateRunNameMock = (runId: string, newName: string): CustomRun | null => {
  const run = customRuns.find((customRun) => customRun.id === runId);
  if (!run) {
    console.error('[experimentMockStore] 런을 찾을 수 없음:', runId);
    return null;
  }

  run.name = newName;
  run.updatedAt = Date.now();
  saveCustomRuns(customRuns);

  return run;
};

// 프로젝트 상세 조회
export const getProjectDetailMock = (projectId: number): CustomProject | null => {
  return customProjects.find((p) => p.id === projectId) || null;
};

// 런 상세 조회
export const getRunDetailMock = (runId: string): any => {
  // 커스텀 런에서 찾기
  const latestCustomRuns = loadCustomRuns();
  const customRun = latestCustomRuns.find((r) => r.id === runId);

  if (customRun) {
    const datasetAsset = findDatasetAsset(customRun);
    const featureSetAsset = findFeatureSetAsset(customRun);
    const parameterSet =
      customRun.parameterSet ?? resolveParameterSet(customRun.parameterSetId, [...mockRunStore, ...latestCustomRuns]);

    const now = new Date().toISOString();

    return {
      id: customRun.id,
      name: customRun.name,
      status: customRun.status,
      startTime: customRun.startTime,
      endTime: customRun.endTime,
      projectId: customRun.projectId,
      projectName: customRun.projectName,
      experimentId: customRun.experimentId,
      experimentName: customRun.experimentName,
      computingResource: customRun.computingResource,
      model: customRun.model ?? (customRun.status === 'Execution_Succeeded' ? createDemoRunModel(customRun.id) : null),
      dataset: datasetAsset,
      featureSet: featureSetAsset,
      parameterSet,
      artifacts: customRun.artifacts
        ? {
            id: parseInt(customRun.id, 10) + 2000,
            runUuid: customRun.id,
            description: undefined,
            files: customRun.artifacts,
            createdAt: now,
            creator: 'admin',
            updatedAt: now,
            updater: 'admin',
            isLiked: false,
          }
        : null,
      metrics: customRun.metrics,
    };
  }

  // 기존 런에서 찾기
  const run = mockRunStore.find((r) => r.id === runId);
  if (!run) return null;

  const endTime =
    run.status === 'Execution_Succeeded'
      ? new Date(new Date(run.startTime).getTime() + 1000 * 60 * 20).toISOString()
      : undefined;

  const now = new Date().toISOString();

  return {
    id: run.id,
    name: run.name,
    status: run.status,
    startTime: run.startTime,
    endTime,
    projectId: run.projectId,
    projectName: run.projectName,
    experimentId: run.experimentId,
    experimentName: run.experimentName,
    computingResource: 'basic',
    model: getDemoRunModel(run),
    dataset: run.datasetAsset,
    featureSet: run.featureSetAsset,
    parameterSet: run.parameterSet,
    artifacts: run.artifacts
      ? {
          id: run.artifacts.id,
          runUuid: run.id,
          description: run.artifacts.description || null,
          files: run.artifacts.files,
          createdAt: now,
          creator: 'admin',
          updatedAt: now,
          updater: null,
          isLiked: false,
        }
      : null,
    metrics: run.metrics,
  };
};

export const getRunLatestExecutionMock = (runId: string | number): any => {
  const latestDemoExecution = getLatestDemoExecutionForRun(runId);
  if (latestDemoExecution) {
    return cloneExecution(latestDemoExecution);
  }

  const run = findRunWithAssetsById(runId);

  if (!run && String(runId) === '1020') {
    return createDemoWorkbenchExecutionPayload(runId);
  }

  if (!run || (run.status === 'Execution_Ready' && !run.metrics)) {
    return null;
  }

  const numericRunId = Number(run.id);
  const endedAt = getRunEndTime(run);
  const status = toExecutionStatus(run.status);

  return {
    id: Number.isFinite(numericRunId) ? numericRunId * 1000 + 1 : 1,
    runId: numericRunId,
    code: DEMO_RUN_WORKBENCH_CODE,
    files: { ...DEMO_RUN_WORKBENCH_FILES },
    status,
    k8SJobName: `demo-run-${run.id}`,
    k8SNamespace: 'cyclone-demo',
    configmapName: `demo-run-${run.id}-config`,
    resourceSpec: {
      cpu: '2',
      memory: '4Gi',
      gpu: '0',
    },
    queuedAt: run.startTime,
    startedAt: status === 'execution_queued' ? null : run.startTime,
    endedAt,
    errorMessage: status === 'execution_failed' ? '데모 실행이 실패했습니다.' : null,
    error_message: status === 'execution_failed' ? '데모 실행이 실패했습니다.' : null,
    sourceRunId: String(run.id),
    source_run_id: String(run.id),
    createdAt: run.startTime,
    installedLibraries: DEMO_RUN_INSTALLED_LIBRARIES.map((library) => ({ ...library })),
  };
};

export const getRunMetricsMock = (sourceRunId: string | number): any => {
  getLatestDemoExecutionForRun(sourceRunId);

  const run = findRunWithAssetsById(sourceRunId);

  if (!run) {
    return null;
  }

  const metrics = run.metrics ?? {};

  return {
    sourceRunId: String(sourceRunId),
    final: metrics,
    history: toMetricHistory(metrics),
  };
};

const buildExperimentTree = (runs: MockRunWithAssets[]): ProjectsExperimentsResponse => {
  const projectMap = new Map<number, ProjectInfo>();

  // 먼저 runs에서 프로젝트와 실험 생성
  runs.forEach((run) => {
    if (!projectMap.has(run.projectId)) {
      projectMap.set(run.projectId, {
        id: run.projectId,
        name: run.projectName,
        experiments: [],
      });
    }

    const project = projectMap.get(run.projectId)!;
    let experiment = project.experiments.find((e) => e.id === run.experimentId);

    if (!experiment) {
      const override = experimentOverrides.get(run.experimentId);
      experiment = {
        id: run.experimentId,
        name: override?.name || run.experimentName,
        runCount: 0,
      };
      project.experiments.push(experiment);
    }

    experiment.runCount += 1;
  });

  // 커스텀 프로젝트 추가 (실험이 없는 빈 프로젝트)
  // localStorage에서 최신 데이터 다시 로드
  const latestCustomProjects = loadCustomProjects();
  latestCustomProjects.forEach((customProject) => {
    if (!projectMap.has(customProject.id)) {
      projectMap.set(customProject.id, {
        id: customProject.id,
        name: customProject.name,
        experiments: [],
      });
    }
  });

  // 커스텀 실험 추가
  const latestCustomExperiments = loadCustomExperiments();
  latestCustomExperiments.forEach((customExp) => {
    // 해당 프로젝트가 있는지 확인
    let project = projectMap.get(customExp.projectId);
    if (!project) {
      // 프로젝트가 없으면 생성 (커스텀 프로젝트일 수도 있음)
      const customProj = latestCustomProjects.find((p) => p.id === customExp.projectId);
      if (customProj) {
        project = {
          id: customProj.id,
          name: customProj.name,
          experiments: [],
        };
        projectMap.set(customProj.id, project);
      } else {
        return;
      }
    }

    // 실험 추가 (runCount는 0)
    const existingExp = project.experiments.find((e) => e.id === customExp.id);
    if (!existingExp) {
      project.experiments.push({
        id: customExp.id,
        name: customExp.name,
        runCount: 0,
      });
    }
  });

  // 커스텀 런 카운트 반영
  const latestCustomRuns = loadCustomRuns();
  latestCustomRuns.forEach((customRun) => {
    const project = projectMap.get(customRun.projectId);
    if (project) {
      const experiment = project.experiments.find((e) => e.id === customRun.experimentId);
      if (experiment) {
        experiment.runCount += 1;
      }
    }
  });

  return {
    projects: Array.from(projectMap.values()),
  };
};

export const getProjectsExperimentsMockData = (query?: string): ProjectsExperimentsResponse => {
  if (!query) {
    return buildExperimentTree(mockRunStore);
  }

  const normalizedQuery = query.toLowerCase();
  const filteredRuns = mockRunStore.filter(
    (run) =>
      run.projectName.toLowerCase().includes(normalizedQuery) ||
      run.experimentName.toLowerCase().includes(normalizedQuery) ||
      run.name.toLowerCase().includes(normalizedQuery)
  );

  return buildExperimentTree(filteredRuns);
};

export const searchIntegrationMockData = (params: {
  query?: string;
  size?: number;
  cursor?: string | null;
  assetTypes?: string[];
  projectId?: number | string;
  experimentId?: number | string;
  runId?: number | string;
  sortBy?: string;
  sortOrder?: string;
}): IntegrationSearchResponse => {
  let filteredAssets = STANDALONE_ASSETS;

  if (params.query) {
    const normalizedQuery = params.query.toLowerCase();
    filteredAssets = filteredAssets.filter((asset) => {
      const searchableText = [
        asset.title,
        asset.description,
        asset.dataset?.table,
        asset.dataset?.namespace,
        asset.featureSet?.table,
        asset.featureSet?.namespace,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchableText.includes(normalizedQuery);
    });
  }

  const featureSetResults = filteredAssets.filter((asset) => asset.type === 'feature_set');
  const datasetResults = filteredAssets.filter((asset) => asset.type === 'dataset');

  const projectsData = getProjectsExperimentsMockData();
  let projectResults: AssetSearchResultItem[] = [];
  let experimentResults: AssetSearchResultItem[] = [];

  if (!params.projectId && !params.experimentId && !params.runId) {
    let filteredProjects = projectsData.projects;

    if (params.query) {
      const normalizedQuery = params.query.toLowerCase();
      filteredProjects = filteredProjects.filter((project) => project.name.toLowerCase().includes(normalizedQuery));
    }

    projectResults = filteredProjects.map((project) => ({
      type: 'project',
      id: project.id,
      title: project.name,
      description: undefined,
      path: [{ type: 'project', id: project.id, label: project.name, to: `/experiments/${project.id}` }],
      isLiked: false,
      creator: 'system',
      updatedAt: new Date().toISOString(),
      createdAt: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(),
      project: {
        id: project.id,
        projectName: project.name,
      },
      experiment: undefined,
      run: undefined,
      dataset: undefined,
      featureSet: undefined,
      parameterSet: undefined,
      artifacts: undefined,
      metrics: undefined,
      experimentTags: null,
    }));
  }

  if (!params.experimentId && !params.runId) {
    let allExperiments = projectsData.projects.flatMap((project) =>
      project.experiments.map((experiment) => ({
        ...experiment,
        projectId: project.id,
        projectName: project.name,
      }))
    );

    if (params.projectId) {
      const projectId = toNullableNumber(params.projectId);
      allExperiments = allExperiments.filter((experiment) => experiment.projectId === projectId);
    }

    if (params.query) {
      const normalizedQuery = params.query.toLowerCase();
      allExperiments = allExperiments.filter((experiment) => experiment.name.toLowerCase().includes(normalizedQuery));
    }

    experimentResults = allExperiments.map((experiment) => ({
      type: 'experiment',
      id: experiment.id,
      title: experiment.name,
      description: undefined,
      path: [
        {
          type: 'project',
          id: experiment.projectId,
          label: experiment.projectName,
          to: `/experiments/${experiment.projectId}`,
        },
        {
          type: 'experiment',
          id: experiment.id,
          label: experiment.name,
          to: `/experiments/${experiment.projectId}/${experiment.id}`,
        },
      ],
      isLiked: false,
      creator: 'system',
      updatedAt: new Date().toISOString(),
      createdAt: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
      project: {
        id: experiment.projectId,
        projectName: experiment.projectName,
      },
      experiment: {
        id: experiment.id,
        experimentName: experiment.name,
        description: undefined,
        runsCount: experiment.runCount,
        lastRuntime: new Date().toISOString(),
      },
      run: undefined,
      dataset: undefined,
      featureSet: undefined,
      parameterSet: undefined,
      artifacts: undefined,
      metrics: undefined,
      experimentTags: null,
    }));
  }

  // Run 필터링 (기존 런 + 커스텀 런)
  const latestCustomRuns = loadCustomRuns();

  // 커스텀 런을 MockRunWithAssets 형식으로 변환
  const customRunsAsMock = latestCustomRuns.map((customRun) => toMockRunWithAssets(customRun, latestCustomRuns));

  let filteredRuns: MockRunWithAssets[] = [...mockRunStore, ...customRunsAsMock];

  if (params.query) {
    const normalizedQuery = params.query.toLowerCase();
    filteredRuns = filteredRuns.filter(
      (run) =>
        run.name.toLowerCase().includes(normalizedQuery) ||
        run.experimentName.toLowerCase().includes(normalizedQuery) ||
        run.projectName.toLowerCase().includes(normalizedQuery)
    );
  }

  if (params.projectId) {
    const projectId = toNullableNumber(params.projectId);
    filteredRuns = filteredRuns.filter((run) => run.projectId === projectId);
  }

  if (params.experimentId) {
    const experimentId = toNullableNumber(params.experimentId);
    filteredRuns = filteredRuns.filter((run) => run.experimentId === experimentId);
  }

  if (params.runId) {
    filteredRuns = filteredRuns.filter((run) => String(run.id) === String(params.runId));
  }

  if (params.assetTypes && params.assetTypes.length > 0) {
    const hasRun = params.assetTypes.includes('run');
    const hasRunDataset = params.assetTypes.includes('run_dataset');
    const hasRunFeatureSet = params.assetTypes.includes('run_feature_set');
    const hasParameterSet =
      params.assetTypes.includes('parameter_set') || params.assetTypes.includes('run_parameter_set');
    const hasArtifact = params.assetTypes.includes('artifact') || params.assetTypes.includes('run_artifact');

    // Run, ParameterSet, Artifact 중 하나라도 필터에 포함되면 런 데이터 필요
    if (!hasRun && !hasRunDataset && !hasRunFeatureSet && !hasParameterSet && !hasArtifact) {
      const hasExperimentTypes = params.assetTypes.some((type) => ['project', 'experiment'].includes(type));
      if (!hasExperimentTypes) {
        filteredRuns = [];
      }
    }
  }

  // 모든 결과 병합
  const runResults: AssetSearchResultItem[] = filteredRuns.map((run) => {
    const endTime =
      run.status === 'Execution_Succeeded'
        ? new Date(new Date(run.startTime).getTime() + 1000 * 60 * 20).toISOString()
        : undefined;

    return {
      type: 'run',
      id: Number(run.id),
      title: run.name,
      description: undefined,
      path: [
        { type: 'project', id: run.projectId, label: run.projectName, to: `/experiments/${run.projectId}` },
        {
          type: 'experiment',
          id: run.experimentId,
          label: run.experimentName,
          to: `/experiments/${run.projectId}/${run.experimentId}`,
        },
        {
          type: 'run',
          id: Number(run.id),
          label: run.name,
          to: `/experiments/${run.projectId}/${run.experimentId}/${run.id}?assetType=run`,
        },
      ],
      isLiked: false,
      creator: 'test user',
      updater: undefined,
      createdAt: run.startTime,
      updatedAt: run.startTime,
      project: {
        id: run.projectId,
        projectName: run.projectName,
      },
      experiment: {
        id: run.experimentId,
        experimentName: run.experimentName,
        description: undefined,
        runsCount: 2,
        lastRuntime: run.startTime,
      },
      run: {
        id: Number(run.id),
        runName: run.name,
        description: undefined,
        startTime: run.startTime,
        endTime,
        status: run.status as any,
      },
      dataset: run.datasetAsset?.dataset || undefined,
      featureSet: run.featureSetAsset?.featureSet || undefined,
      parameterSet: run.parameterSet,
      artifacts: run.artifacts,
      metrics: run.metrics,
      experimentTags: toExperimentTagList(run.experimentTags),
    };
  });

  // Query 필터링 적용
  const queryFilteredRuns = filteredRuns;

  const runDatasetResults: AssetSearchResultItem[] = queryFilteredRuns
    .filter((run) => !!run.datasetAsset?.dataset)
    .map((run) => {
      const datasetAsset = run.datasetAsset!;
      const dataset = datasetAsset.dataset!;
      const endTime =
        run.status === 'Execution_Succeeded'
          ? new Date(new Date(run.startTime).getTime() + 1000 * 60 * 20).toISOString()
          : undefined;

      return {
        ...datasetAsset,
        type: 'run_dataset',
        id: datasetAsset.id,
        title: dataset.table || datasetAsset.title,
        description: dataset.description || datasetAsset.description,
        path: [
          { type: 'project', id: run.projectId, label: run.projectName, to: `/experiments/${run.projectId}` },
          {
            type: 'experiment',
            id: run.experimentId,
            label: run.experimentName,
            to: `/experiments/${run.projectId}/${run.experimentId}`,
          },
          {
            type: 'run',
            id: Number(run.id),
            label: run.name,
            to: `/experiments/${run.projectId}/${run.experimentId}/${run.id}?assetType=run`,
          },
          {
            type: 'run_dataset',
            id: datasetAsset.id,
            label: dataset.table || datasetAsset.title,
            to: null,
          },
        ],
        createdAt: dataset.createdAt || run.startTime,
        updatedAt: dataset.updatedAt || dataset.createdAt || run.startTime,
        project: {
          id: run.projectId,
          projectName: run.projectName,
        },
        experiment: {
          id: run.experimentId,
          experimentName: run.experimentName,
          description: undefined,
          runsCount: 2,
          lastRuntime: run.startTime,
        },
        run: {
          id: Number(run.id),
          runName: run.name,
          description: undefined,
          startTime: run.startTime,
          endTime,
          status: run.status as any,
        },
        dataset,
        featureSet: undefined,
        parameterSet: run.parameterSet,
        artifacts: run.artifacts,
        metrics: run.metrics,
        experimentTags: toExperimentTagList(run.experimentTags),
      };
    });

  const runFeatureSetResults: AssetSearchResultItem[] = queryFilteredRuns
    .filter((run) => !!run.featureSetAsset?.featureSet)
    .map((run) => {
      const featureSetAsset = run.featureSetAsset!;
      const featureSet = featureSetAsset.featureSet!;
      const endTime =
        run.status === 'Execution_Succeeded'
          ? new Date(new Date(run.startTime).getTime() + 1000 * 60 * 20).toISOString()
          : undefined;

      return {
        ...featureSetAsset,
        type: 'run_feature_set',
        id: featureSetAsset.id,
        title: featureSet.table || featureSetAsset.title,
        description: featureSet.description || featureSetAsset.description,
        path: [
          { type: 'project', id: run.projectId, label: run.projectName, to: `/experiments/${run.projectId}` },
          {
            type: 'experiment',
            id: run.experimentId,
            label: run.experimentName,
            to: `/experiments/${run.projectId}/${run.experimentId}`,
          },
          {
            type: 'run',
            id: Number(run.id),
            label: run.name,
            to: `/experiments/${run.projectId}/${run.experimentId}/${run.id}?assetType=run`,
          },
          {
            type: 'run_feature_set',
            id: featureSetAsset.id,
            label: featureSet.table || featureSetAsset.title,
            to: null,
          },
        ],
        createdAt: featureSet.createdAt || run.startTime,
        updatedAt: featureSet.updatedAt || featureSet.createdAt || run.startTime,
        project: {
          id: run.projectId,
          projectName: run.projectName,
        },
        experiment: {
          id: run.experimentId,
          experimentName: run.experimentName,
          description: undefined,
          runsCount: 2,
          lastRuntime: run.startTime,
        },
        run: {
          id: Number(run.id),
          runName: run.name,
          description: undefined,
          startTime: run.startTime,
          endTime,
          status: run.status as any,
        },
        dataset: undefined,
        featureSet,
        parameterSet: run.parameterSet,
        artifacts: run.artifacts,
        metrics: run.metrics,
        experimentTags: toExperimentTagList(run.experimentTags),
      };
    });

  // 각 런의 ParameterSet을 독립적인 검색 결과로 추가 (parameterSet이 있는 런만)
  const parameterSetResults: AssetSearchResultItem[] = queryFilteredRuns
    .filter((run) => {
      // ParameterSet이 없는 런은 제외
      if (!run.parameterSet) return false;

      // Query 필터링
      if (params.query) {
        const normalizedQuery = params.query.toLowerCase();
        const title = `${run.name}의 파라미터셋`.toLowerCase();
        if (!title.includes(normalizedQuery)) {
          // ParameterSet의 hyperparameters에서도 검색
          const hyperparamValues = Object.values(run.parameterSet.hyperparameters).join(' ').toLowerCase();
          if (!hyperparamValues.includes(normalizedQuery)) {
            return false;
          }
        }
      }
      return true;
    })
    .map((run) => {
      const endTime =
        run.status === 'Execution_Succeeded'
          ? new Date(new Date(run.startTime).getTime() + 1000 * 60 * 20).toISOString()
          : undefined;
      const paramSet = run.parameterSet!;

      // ParameterSet은 런보다 2-1초 먼저 생성됨
      const paramSetCreatedAt = new Date(new Date(run.startTime).getTime() - 2000).toISOString();
      const paramSetUpdatedAt = new Date(new Date(run.startTime).getTime() - 1000).toISOString();

      return {
        type: 'parameter_set',
        id: paramSet.id,
        title: `${run.name}의 파라미터셋`,
        description: undefined,
        path: [
          { type: 'project', id: run.projectId, label: run.projectName, to: `/experiments/${run.projectId}` },
          {
            type: 'experiment',
            id: run.experimentId,
            label: run.experimentName,
            to: `/experiments/${run.projectId}/${run.experimentId}`,
          },
          {
            type: 'run',
            id: Number(run.id),
            label: run.name,
            to: `/experiments/${run.projectId}/${run.experimentId}/${run.id}?assetType=run`,
          },
          {
            type: 'parameter_set',
            id: paramSet.id,
            label: 'parameter_set',
            to: null,
          },
        ],
        isLiked: false,
        creator: 'test user',
        updater: 'system',
        createdAt: paramSetCreatedAt,
        updatedAt: paramSetUpdatedAt,
        project: {
          id: run.projectId,
          projectName: run.projectName,
        },
        experiment: {
          id: run.experimentId,
          experimentName: run.experimentName,
          description: undefined,
          runsCount: 2,
          lastRuntime: run.startTime,
        },
        run: {
          id: Number(run.id),
          runName: run.name,
          description: undefined,
          startTime: run.startTime,
          endTime,
          status: run.status as any,
        },
        dataset: run.datasetAsset?.dataset || undefined,
        featureSet: run.featureSetAsset?.featureSet || undefined,
        parameterSet: paramSet,
        artifacts: run.artifacts,
        metrics: run.metrics,
        experimentTags: toExperimentTagList(run.experimentTags),
      };
    });

  // 각 런의 Artifacts를 독립적인 검색 결과로 추가 (artifacts가 있는 런만)
  const artifactResults: AssetSearchResultItem[] = queryFilteredRuns
    .filter((run) => {
      // Artifacts가 없는 런은 제외
      if (!run.artifacts) return false;

      // Query 필터링
      if (params.query) {
        const normalizedQuery = params.query.toLowerCase();
        const title = `${run.name}의 아티팩트`.toLowerCase();
        if (!title.includes(normalizedQuery)) {
          // Artifacts의 files에서도 검색
          const fileNames = run.artifacts.files
            .map((f) => f.path)
            .join(' ')
            .toLowerCase();
          if (!fileNames.includes(normalizedQuery)) {
            return false;
          }
        }
      }
      return true;
    })
    .map((run) => {
      const endTime =
        run.status === 'Execution_Succeeded'
          ? new Date(new Date(run.startTime).getTime() + 1000 * 60 * 20).toISOString()
          : undefined;
      const arts = run.artifacts!;

      // Artifact는 런보다 4-3초 먼저 생성됨
      const artifactCreatedAt = new Date(new Date(run.startTime).getTime() - 4000).toISOString();
      const artifactUpdatedAt = new Date(new Date(run.startTime).getTime() - 3000).toISOString();

      return {
        type: 'artifact',
        id: arts.id,
        title: `${run.name}의 아티팩트`,
        description: undefined,
        path: [
          { type: 'project', id: run.projectId, label: run.projectName, to: `/experiments/${run.projectId}` },
          {
            type: 'experiment',
            id: run.experimentId,
            label: run.experimentName,
            to: `/experiments/${run.projectId}/${run.experimentId}`,
          },
          {
            type: 'run',
            id: Number(run.id),
            label: run.name,
            to: `/experiments/${run.projectId}/${run.experimentId}/${run.id}?assetType=run`,
          },
          {
            type: 'artifact',
            id: arts.id,
            label: 'artifact',
            to: null,
          },
        ],
        isLiked: false,
        creator: 'test user',
        updater: undefined,
        createdAt: artifactCreatedAt,
        updatedAt: artifactUpdatedAt,
        project: {
          id: run.projectId,
          projectName: run.projectName,
        },
        experiment: {
          id: run.experimentId,
          experimentName: run.experimentName,
          description: undefined,
          runsCount: 2,
          lastRuntime: run.startTime,
        },
        run: {
          id: Number(run.id),
          runName: run.name,
          description: undefined,
          startTime: run.startTime,
          endTime,
          status: run.status as any,
        },
        dataset: run.datasetAsset?.dataset || undefined,
        featureSet: run.featureSetAsset?.featureSet || undefined,
        parameterSet: run.parameterSet,
        artifacts: arts,
        metrics: run.metrics,
        experimentTags: toExperimentTagList(run.experimentTags),
      };
    });

  const runParameterSetResults: AssetSearchResultItem[] = parameterSetResults.map((result) => ({
    ...result,
    type: 'run_parameter_set',
    path: result.path.map((pathItem) =>
      pathItem.type === 'parameter_set' ? { ...pathItem, type: 'run_parameter_set' } : pathItem
    ),
  }));

  const runArtifactResults: AssetSearchResultItem[] = artifactResults.map((result) => ({
    ...result,
    type: 'run_artifact',
    path: result.path.map((pathItem) =>
      pathItem.type === 'artifact' ? { ...pathItem, type: 'run_artifact' } : pathItem
    ),
  }));

  // AssetTypes 필터링에 따라 결과 타입 선택
  let allResults: AssetSearchResultItem[] = [];

  if (params.assetTypes && params.assetTypes.length > 0) {
    if (params.assetTypes.includes('project')) {
      allResults.push(...projectResults);
    }

    if (params.assetTypes.includes('experiment')) {
      allResults.push(...experimentResults);
    }

    if (params.assetTypes.includes('run')) {
      allResults.push(...runResults);
    }

    if (params.assetTypes.includes('run_dataset')) {
      allResults.push(...runDatasetResults);
    }

    if (params.assetTypes.includes('run_feature_set')) {
      allResults.push(...runFeatureSetResults);
    }

    if (params.assetTypes.includes('parameter_set')) {
      allResults.push(...parameterSetResults);
    }

    if (params.assetTypes.includes('run_parameter_set')) {
      allResults.push(...runParameterSetResults);
    }

    if (params.assetTypes.includes('artifact')) {
      allResults.push(...artifactResults);
    }

    if (params.assetTypes.includes('run_artifact')) {
      allResults.push(...runArtifactResults);
    }

    if (params.assetTypes.includes('feature_set')) {
      allResults.push(...featureSetResults);
    }

    if (params.assetTypes.includes('dataset')) {
      allResults.push(...datasetResults);
    }
  } else {
    // 필터가 없으면 모든 결과 포함 (데이터 탐색에서는 parameter_set 단독 행 제외)
    allResults = [
      ...projectResults,
      ...experimentResults,
      ...runResults,
      ...artifactResults,
      ...featureSetResults,
      ...datasetResults,
    ];
  }

  // 정렬
  if (params.sortBy === 'name') {
    allResults.sort((a, b) => a.title.localeCompare(b.title));
  } else {
    allResults.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }

  if (params.sortOrder === 'asc') {
    allResults.reverse();
  }

  // undefined 제거
  allResults = allResults.filter(Boolean);

  // 페이지네이션
  const size = params.size || 15;
  const cursorIndex = params.cursor ? Number(params.cursor) : 0;
  const results = allResults.slice(cursorIndex, cursorIndex + size);
  const hasMore = cursorIndex + size < allResults.length;
  const nextCursor = hasMore ? String(cursorIndex + size) : null;

  return {
    results,
    totalCount: allResults.length,
    nextCursor,
    hasMore,
  };
};

export const resetExperimentMockStore = () => {
  mockRunStore = [...INITIAL_MOCK_RUNS];
  customProjects = [];
  customExperiments = [];
  customRuns = [];
  demoExecutions = [];
  experimentOverrides.clear();

  try {
    localStorage.removeItem(PROJECTS_STORAGE_KEY);
    localStorage.removeItem(EXPERIMENTS_STORAGE_KEY);
    localStorage.removeItem(RUNS_STORAGE_KEY);
    localStorage.removeItem(EXECUTIONS_STORAGE_KEY);
  } catch {
    // 테스트/비브라우저 환경에서 localStorage 접근 실패는 무시합니다.
  }
};

export const getProjectMockData = (projectId: number): any => {
  // 먼저 커스텀 프로젝트에서 찾기 (최신 데이터 로드)
  const latestCustomProjects = loadCustomProjects();
  const customProject = latestCustomProjects.find((p) => p.id === projectId);
  if (customProject) {
    return {
      id: customProject.id,
      name: customProject.name,
      description: customProject.description,
    };
  }

  // 기존 프로젝트에서 찾기
  const project = mockRunStore.find((run) => run.projectId === projectId);

  if (!project) {
    return null;
  }

  return {
    id: projectId,
    name: project.projectName,
    description: '터빈 설비 건강 모니터링 및 이상 진단 분석 프로젝트',
  };
};

export const getExperimentsMockData = (params?: { projectId?: number; query?: string }): any => {
  let filteredRuns = mockRunStore;

  if (params?.projectId) {
    filteredRuns = filteredRuns.filter((run) => run.projectId === params.projectId);
  }

  if (params?.query) {
    const normalizedQuery = params.query.toLowerCase();
    filteredRuns = filteredRuns.filter(
      (run) =>
        run.experimentName.toLowerCase().includes(normalizedQuery) || run.name.toLowerCase().includes(normalizedQuery)
    );
  }

  const experimentMap = new Map<number, any>();

  filteredRuns.forEach((run) => {
    if (!experimentMap.has(run.experimentId)) {
      const override = experimentOverrides.get(run.experimentId);
      experimentMap.set(run.experimentId, {
        id: run.experimentId,
        name: override?.name || run.experimentName,
        description: override?.description ?? null,
        creator: 'system',
        createdAt: run.startTime,
        updater: 'system',
        updatedAt: override ? new Date(override.updatedAt).toISOString() : run.startTime,
        runCount: 0,
      });
    }

    const exp = experimentMap.get(run.experimentId)!;
    exp.runCount += 1;
  });

  // 커스텀 실험 추가 (runCount는 0)
  const latestCustomExperiments = loadCustomExperiments();

  latestCustomExperiments.forEach((customExp) => {
    // projectId 필터가 있으면 해당 프로젝트의 실험만 추가
    if (params?.projectId && customExp.projectId !== params.projectId) {
      return;
    }

    // query 필터가 있으면 이름 검색
    if (params?.query) {
      const normalizedQuery = params.query.toLowerCase();
      if (!customExp.name.toLowerCase().includes(normalizedQuery)) {
        return;
      }
    }

    if (!experimentMap.has(customExp.id)) {
      experimentMap.set(customExp.id, {
        id: customExp.id,
        name: customExp.name,
        description: customExp.description || null,
        creator: customExp.creator,
        createdAt: new Date(customExp.createdAt).toISOString(),
        updater: customExp.updater,
        updatedAt: new Date(customExp.updatedAt).toISOString(),
        runCount: 0,
      });
    }
  });

  // 커스텀 런의 runCount 반영
  const latestCustomRuns = loadCustomRuns();

  latestCustomRuns.forEach((customRun) => {
    // projectId 필터가 있으면 해당 프로젝트의 런만 카운트
    if (params?.projectId && customRun.projectId !== params.projectId) {
      return;
    }

    const experiment = experimentMap.get(customRun.experimentId);
    if (experiment) {
      experiment.runCount += 1;
    }
  });

  const experiments = Array.from(experimentMap.values()).sort((a, b) => a.id - b.id);

  return {
    pagination: {
      page: 1,
      size: 100,
      totalCount: experiments.length,
      totalPages: 1,
    },
    experiments,
  };
};

export const getRunsMockData = (params?: { experimentId?: number; query?: string }): any => {
  // 커스텀 런을 MockRunWithAssets 형식으로 변환
  const latestCustomRuns = loadCustomRuns();
  const customRunsAsMock = latestCustomRuns.map((customRun) => toMockRunWithAssets(customRun, latestCustomRuns));

  let filteredRuns: MockRunWithAssets[] = [...mockRunStore, ...customRunsAsMock];

  if (params?.experimentId) {
    filteredRuns = filteredRuns.filter((run) => run.experimentId === params.experimentId);
  }

  if (params?.query) {
    const normalizedQuery = params.query.toLowerCase();
    filteredRuns = filteredRuns.filter((run) => run.name.toLowerCase().includes(normalizedQuery));
  }

  const runs = filteredRuns
    .map((run) => {
      // status가 유효하지 않으면 기본값 사용
      let validStatus = run.status;
      const validStatuses = ['Execution_Ready', 'Execution_Succeeded', 'Execution_Failed', 'Execution_Canceled'];
      if (!validStatuses.includes(run.status)) {
        validStatus = 'Execution_Ready';
      }

      return {
        id: Number(run.id),
        name: run.name,
        experimentName: run.experimentName,
        description: null,
        status: validStatus,
        path: [
          { type: 'project', id: run.projectId, label: run.projectName, to: `/experiments/${run.projectId}` },
          {
            type: 'experiment',
            id: run.experimentId,
            label: run.experimentName,
            to: `/experiments/${run.projectId}/${run.experimentId}`,
          },
          { type: 'run', id: Number(run.id), label: run.name, to: null },
        ],
        metrics: run.metrics || null,
        artifacts: run.artifacts, // MLflow Mock Client가 이 필드를 사용합니다
        createdAt: run.startTime,
        updatedAt: run.startTime,
        creator: 'system',
        updater: 'system',
      };
    })
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  return {
    pagination: {
      page: 1,
      size: 100,
      totalCount: runs.length,
      totalPages: 1,
    },
    runs,
  };
};

export const getExperimentRunsMockData = (experimentId: number): any => {
  const runs = mockRunStore
    .filter((run) => run.experimentId === experimentId)
    .map((run) => ({
      id: Number(run.id),
      name: run.name,
      status: run.status,
      createdAt: run.startTime,
    }));

  return {
    runs,
    total: runs.length,
  };
};

export const compareRunsMockData = (runIds: number[]): any => {
  const runs = runIds
    .map((runId) => {
      const run = mockRunStore.find((r) => Number(r.id) === runId);
      if (!run) return null;

      return getExperimentAssetDetail(runId, 'run');
    })
    .filter((r) => r !== null);

  return { runs };
};

export const getExperimentAssetDetail = (
  assetId: number,
  type: 'run' | 'dataset' | 'feature_set' | 'parameter_set' | 'artifact'
): any => {
  if (type === 'feature_set' || type === 'dataset') {
    const asset = STANDALONE_ASSETS.find((a) => a.id === assetId);
    if (!asset) return null;

    const convertColumnsToColumnInfo = (columns: string[]) => {
      return columns.map((colName) => ({
        name: colName,
        type: 'STRING',
      }));
    };

    return {
      id: asset.id,
      type: asset.type,
      title: asset.title,
      description: asset.description,
      path: asset.path,
      isLiked: asset.isLiked,
      createdAt: asset.createdAt,
      creator: 'system',
      updatedAt: asset.updatedAt,
      updater: 'system',
      project: asset.project,
      experiment: asset.experiment,
      run: asset.run,
      dataset: asset.dataset
        ? {
            ...asset.dataset,
            columns: convertColumnsToColumnInfo(asset.dataset.columns),
          }
        : null,
      featureSet: asset.featureSet
        ? {
            ...asset.featureSet,
            columns: convertColumnsToColumnInfo(asset.featureSet.columns),
          }
        : null,
      parameterSet: null,
      metrics: null,
      artifacts: null,
      experimentTags: null,
    };
  }

  // 기존 런 + 커스텀 런에서 검색
  const latestCustomRuns = loadCustomRuns();
  const customRunsAsMock = latestCustomRuns.map((customRun) => toMockRunWithAssets(customRun, latestCustomRuns));

  const allRuns: MockRunWithAssets[] = [...mockRunStore, ...customRunsAsMock];

  const run = allRuns.find((r) => {
    if (type === 'run') return String(r.id) === String(assetId);
    if (type === 'parameter_set') return r.parameterSet?.id === assetId;
    if (type === 'artifact') return r.artifacts?.id === assetId;
    return false;
  });

  if (!run) return null;

  const endTime =
    run.status === 'Execution_Succeeded'
      ? new Date(new Date(run.startTime).getTime() + 1000 * 60 * 20).toISOString()
      : undefined;

  const convertColumnsToColumnInfo = (columns: string[]) => {
    return columns.map((colName) => ({
      name: colName,
      type: 'STRING',
    }));
  };

  const baseResponse = {
    id: assetId,
    type,
    title: type === 'run' ? run.name : type === 'parameter_set' ? `${run.name}의 파라미터셋` : `${run.name}의 아티팩트`,
    description: undefined,
    path: [
      { type: 'project', id: run.projectId, label: run.projectName, to: `/experiments/${run.projectId}` },
      {
        type: 'experiment',
        id: run.experimentId,
        label: run.experimentName,
        to: `/experiments/${run.projectId}/${run.experimentId}`,
      },
      {
        type: 'run',
        id: Number(run.id),
        label: run.name,
        to: `/experiments/${run.projectId}/${run.experimentId}/${run.id}?assetType=run`,
      },
    ],
    isLiked: false,
    createdAt: run.startTime,
    creator: 'test user',
    updatedAt: run.startTime,
    updater: 'system',
    project: {
      projectId: run.projectId,
      projectName: run.projectName,
      description: undefined,
      createdAt: run.startTime,
      creator: 'test user',
      updatedAt: run.startTime,
      updater: 'system',
    },
    experiment: {
      experimentId: run.experimentId,
      experimentName: run.experimentName,
      description: undefined,
      createdAt: run.startTime,
      creator: 'test user',
      updatedAt: run.startTime,
      updater: 'system',
    },
    run: {
      runId: run.id,
      runName: run.name,
      startTime: run.startTime,
      endTime,
      status: run.status,
      createdAt: run.startTime,
      creator: 'test user',
      updatedAt: run.startTime,
      updater: 'system',
      description: undefined,
      isLiked: false,
      model: getDemoRunModel(run),
    },
    dataset: run.datasetAsset?.dataset
      ? {
          ...run.datasetAsset.dataset,
          columns: convertColumnsToColumnInfo(run.datasetAsset.dataset.columns),
        }
      : null,
    featureSet: run.featureSetAsset?.featureSet
      ? {
          ...run.featureSetAsset.featureSet,
          columns: convertColumnsToColumnInfo(run.featureSetAsset.featureSet.columns),
        }
      : null,
    parameterSet: run.parameterSet
      ? {
          ...run.parameterSet,
          createdAt: new Date(new Date(run.startTime).getTime() - 2000).toISOString(),
          creator: 'test user',
          updatedAt: new Date(new Date(run.startTime).getTime() - 1000).toISOString(),
          updater: 'system',
          isLiked: false,
        }
      : null,
    metrics: run.metrics
      ? {
          createdAt: run.startTime,
          creator: 'system',
          updatedAt: run.startTime,
          updater: 'system',
          final: run.metrics,
          history: run.metrics,
        }
      : null,
    artifacts: run.artifacts
      ? {
          ...run.artifacts,
          runUuid: String(run.id),
          createdAt: new Date(new Date(run.startTime).getTime() - 4000).toISOString(),
          creator: 'test user',
          updatedAt: new Date(new Date(run.startTime).getTime() - 3000).toISOString(),
          updater: 'system',
          isLiked: false,
        }
      : null,
    experimentTags: toExperimentTagList(run.experimentTags),
  };

  if (type === 'parameter_set') {
    baseResponse.path.push({
      type: 'parameter_set',
      id: assetId,
      label: 'parameter_set',
      to: null,
    } as any);
  } else if (type === 'artifact') {
    baseResponse.path.push({
      type: 'artifact',
      id: assetId,
      label: 'artifact',
      to: null,
    } as any);
  }

  return baseResponse;
};
